import axios from "axios";
import Axios from "../config/axios-v1-config";
import { errorHandler } from "../utils/errorHandler";

/**
 * Config de axios por request. Solo lleva lo que el llamador pidió: sin
 * opciones queda `{}` y la request sale igual que antes (timeout global de 60 s,
 * sin cancelación).
 * @param {{timeoutMs?: number, signal?: AbortSignal}} [options]
 * @returns {{timeout?: number, signal?: AbortSignal}}
 */
const requestConfig = ({ timeoutMs, signal } = {}) => ({
  ...(Number.isFinite(timeoutMs) && timeoutMs > 0 && { timeout: timeoutMs }),
  ...(signal && { signal }),
});

/** Request abortada por el llamador con su AbortSignal. */
const isRequestCanceled = (err) => axios.isCancel(err) || err?.name === "CanceledError";

/**
 * errorInfo de los clientes de incidencias. Una request abortada (el panel
 * cambió de conversación) no es un error para el usuario: sin toast ni log, y
 * con `canceled: true` para que el llamador la descarte.
 */
const incidentRequestError = (err, { silent, customMessage, context }) => {
  if (isRequestCanceled(err)) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      logError: false,
      context,
    });
    return { ...errorInfo, canceled: true };
  }
  return errorHandler.handleApiError(err, {
    showNotification: !silent,
    customMessage,
    context,
  });
};

/**
 * Paquetes que trajo un retiro, con su estado ACTUAL.
 *
 * El `collectId` sale de `pickups.collectId`, que ya viene en el listado de retiros. Los
 * retiros anteriores al despliegue del vínculo lo tienen en `null`: en ese caso NO hay
 * que llamar acá ni intentar reconstruir el detalle cruzando por fecha + comercio, que es
 * ambiguo justo cuando importa (dos retiros al mismo comercio el mismo día).
 *
 * `silent`: el detalle se abre desde una fila de la tabla y un error puntual no debe
 * tapar la pantalla con un toast; la fila muestra su propio mensaje.
 */
export const getPackagesByCollect = async (collectId, { silent = true } = {}) => {
  try {
    const { data } = await Axios.get(`packages/by-collect/${collectId}`);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    if (!silent) errorHandler(error);
    throw error;
  }
};


export const getAllPackages = async ({
  id,
  page,
  limit,
  query,
  q,
  orderField,
  coms,
  // Nombre de la región (no su código): el backend lo compara contra
  // `shipping_address.city.name`, que es donde el schema guarda la región.
  region,
  // Código postal declarado. CPA ("M5501ABC") o CP4 ("5501"); el backend
  // resuelve que un CP4 encuentre también los CPA que lo contienen.
  zipCode,
  status,
  startDate,
  endDate,
  // `silent`: no muestra el toast de error (para enriquecimientos en segundo
  // plano, p.ej. resolver la nota del conductor parada por parada, donde un 404
  // puntual no debe interrumpir al usuario). Aditivo: default = comportamiento previo.
  silent = false,
}) => {
  try {
    const params = new URLSearchParams({
      page,
      limit,
      orderField,
      sortType: 'desc',
      // `q`: búsqueda unificada parcial sobre followingNumber + order_id +
      // shipping_address.address_line (un solo request al backend). Reemplaza
      // el patrón viejo de 3 requests paralelos. Si se pasa, gana sobre `query`.
      ...(q ? { q } : query && { [query.type]: query.query }),
      ...(coms && { 'shipping_address.municipality.name': coms }),
      ...(region && { 'shipping_address.city.name': region }),
      ...(zipCode && { 'shipping_address.zip_code': zipCode }),
      ...(startDate && { startDateCreation: startDate, endDateCreation: endDate }),
    });

    // Manejar status como array o valor único.
    // OJO: 0 = "Pendiente" es un estado válido; no usar `if (status)` (descarta el 0).
    if (status !== undefined && status !== null && status !== "") {
      if (Array.isArray(status)) {
        status.forEach((s) => params.append("status", s));
      } else {
        params.append("status", status);
      }
    }

    const response = await Axios.get(`packages?${params.toString()}`);

    return { success: true, data: response.data, error: null };
  } catch (err) {
    // Sin customMessage: un texto fijo tapaba el detalle del backend (p. ej. el
    // 400 de `q` con su rango de longitud). `fallbackMessage` deja pasar el
    // texto del body cuando existe y usa el genérico solo si no hay ninguno.
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: !silent,
      fallbackMessage: "Error al cargar los paquetes. Por favor intenta nuevamente.",
      context: "Obtener paquetes"
    });
    
    return { success: false, data: null, error: errorInfo };
  }
};



// Dvlprchris
export const getOnePackage = async (id) => {
  try {
    const packageFound = await Axios.get(`packages/${id}`);
    return { success: true, data: packageFound.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: true,
      customMessage: "Error al cargar el paquete. Por favor intenta nuevamente.",
      context: "Obtener un paquete"
    });
    
    return { success: false, data: null, error: errorInfo };
  }
}

export const countAllPackages = async ({
  id,
  page,
  limit,
  query,
  orderField,
  coms,
  status,
  startDate,
  endDate,
}) => {
  const packages = await Axios.get(
    `countPackages/${id}?&page=${page}&limit=${limit}${query && `&${query.type}=${query.query}`}&orderField=${orderField}&sortType=desc${status ? `&status=${status}` : ""
    }${coms ? `&shipping_address.municipality.name=${coms}` : ""}${startDate
      ? `&startDateCreation=${startDate}&endDateCreation=${endDate}`
      : ""
    }`
  ).catch((err) => {
    return { error: err.response.data };
  });

  return packages;
}

export const getAllPackagesByStatus = async ({
  id,
  state,
  startDate,
  endDate,
}) => {
  const packages = await Axios.get(
    `status-packages/${id}?state=${state}&startDate=${startDate}&endDate=${endDate}`
  ).catch((err) => {
    return { error: err.response.data };
  });

  return packages;
};

export const updatePackageStatus = async ({ status, id }) => {
  const packageUpdated = await Axios.put(`packages/update/${id}`, {
    value: { status },
  }).catch((err) => {
    return { error: err.response.data };
  });

  return packageUpdated;
};

export const createPackage = async ({ values }) => {
  try {
    const packageCreated = await Axios.post(`packages`, values);
    return { success: true, data: packageCreated.data, error: null };
  } catch (err) {
    // Sin customMessage ni toast: un texto fijo acá tapaba el detalle del backend
    // (qué campo falla). El llamador decide cómo mostrarlo; el detalle viaja en
    // `error.data` / `error.status` (ver utils/createPackageErrors.js).
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      context: "Crear paquete"
    });

    return { success: false, data: null, error: errorInfo };
  }
};

/**
 * errorInfo de la etiqueta de devolución y del pedido de cambio con los campos del
 * cuerpo que el llamador necesita para decidir qué mostrar. Las dos cosas se
 * excluyen sobre un mismo padre, y cada 409 trae con qué chocó: el hijo de cambio
 * (`exchange_in_progress`, `returned_by_exchange`: `childId`) o la etiqueta
 * (`return_label_exists`: `returnLabelId`), con su `followingNumber`. El 400
 * `parent_not_delivered` se distingue de uno de validación solo por `code`.
 */
const exchangeErrorInfo = (errorInfo) => ({
  ...errorInfo,
  code: errorInfo?.data?.code ?? null,
  childId: errorInfo?.data?.childId ?? null,
  returnLabelId: errorInfo?.data?.returnLabelId ?? null,
  followingNumber: errorInfo?.data?.followingNumber ?? null,
});

export const createReturnLabel = async (packageId) => {
  try {
    const response = await Axios.post(`packages/${packageId}/return-label`);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    // El llamador (downloadVoucher) muestra `error.message` UNA vez: acá no se
    // tostea, solo se resuelve el texto (backend si lo hay, genérico si no).
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al crear la etiqueta de devolución.",
      context: "Crear etiqueta de devolución"
    });
    return { success: false, data: null, error: exchangeErrorInfo(errorInfo) };
  }
};

/**
 * Crea el pedido hijo de cambio de producto de un pedido entregado
 * (`POST /packages/:id/exchange`, dueño microservice-packages). Lo ausente del
 * body se hereda del padre, salvo el cobro contra entrega, que nunca se hereda.
 * No tostea: el llamador (CreateExchangeDialog) decide el mensaje según `code`.
 * @param {string} parentId - _id del pedido entregado
 * @param {{products?: object[], comments?: string, cashOnDelivery?: {amount: number}}} body
 * @returns {Promise<{success: boolean, data: object|null, error: object|null}>}
 */
export const createExchangePackage = async (parentId, body = {}) => {
  try {
    const response = await Axios.post(`packages/${parentId}/exchange`, body);
    // El contrato es { data: <hijo> }; se tolera el hijo pelado por si un proxy
    // ya lo desenvolvió.
    const payload = response.data;
    return { success: true, data: payload?.data ?? payload, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "No se pudo crear el pedido de cambio.",
      context: "Crear pedido de cambio"
    });
    return { success: false, data: null, error: exchangeErrorInfo(errorInfo) };
  }
};

/**
 * Cadena de cambios de un pedido (`GET /packages/:id/exchanges`, `:id` = padre o
 * hijo): `{ parent, children[], returnedByChildId }`. No tostea: el detalle
 * muestra el error dentro de su propia sección.
 * @param {string} packageId
 * @returns {Promise<{success: boolean, data: object|null, error: object|null}>}
 */
export const getPackageExchanges = async (packageId) => {
  try {
    const response = await Axios.get(`packages/${packageId}/exchanges`);
    const payload = response.data;
    return { success: true, data: payload?.data ?? payload, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "No se pudo cargar el cambio de producto.",
      context: "Cambios del pedido"
    });
    return { success: false, data: null, error: exchangeErrorInfo(errorInfo) };
  }
};

export const loadFile = async ({ file, user }) => {
  const documentLoaded = await Axios.post(`base/add-excel`, {
    bdata: file,
    user,
  }).catch((err) => {
    return { error: err.response.data };
  });

  return documentLoaded;
};

export const loadCustomFile = async ({ file, user }) => {
  const documentLoaded = await Axios.post(`add-custom-excel`, {
    bdata: file,
    user,
  }).catch((err) => {
    return { error: err.response.data };
  });

  return documentLoaded;
};

export const getAdminPackages = async ({
  page,
  limit,
  query,
  q,
  orderField,
  sortType,
  coms,
  // Código postal declarado (CPA o CP4). Ver getAllPackages.
  zipCode,
  status,
  startDate,
  endDate,
  assignTo,
  client,
  updatedAtSince,
  meliStatusMismatch,
  type,
  // Operador logístico al que acotar la consulta. Solo lo respeta el backend
  // para el superadmin del sistema (que consulta todos los operadores); para
  // el resto de los roles su operador sale del token y este param se descarta.
  logisticCompanyId,
  // Opcionales del panel de incidencias: cancelar al cambiar de contexto y un
  // timeout propio. Sin ellos la request sale como siempre.
  signal,
  timeoutMs,
}) => {

  const params = new URLSearchParams();

  params.append("page", page);
  params.append("limit", limit);
  params.append("orderField", orderField);
  params.append("sortType", sortType || "desc");

  if (client) params.append("senderInfo.companyId", client);
  if (assignTo) params.append("assignTo", assignTo);
  // `q`: busqueda unificada parcial sobre followingNumber + order_id +
  // shipping_address.address_line (un solo request al backend). Si se pasa,
  // gana sobre `query` (legacy: 3 requests paralelos por campo).
  if (q) {
    params.append("q", q);
  } else if (query && query.type && query.query) {
    params.append(query.type, query.query);
  }
  if (status !== undefined && status !== null && status !== "") {
    if (Array.isArray(status)) {
      status.forEach((s) => params.append("status", s));
    } else {
      params.append("status", status);
    }
  }
  if (type) params.append("type", type);
  if (coms) params.append("shipping_address.municipality.name", coms);
  if (zipCode) params.append("shipping_address.zip_code", zipCode);
  if (startDate && endDate) {
    params.append("startDateCreation", startDate);
    params.append("endDateCreation", endDate);
  }
  if (updatedAtSince) {
    params.append("updatedAtSince", updatedAtSince);
  }
  if (meliStatusMismatch) {
    params.append("meliStatusMismatch", "true");
  }
  if (logisticCompanyId) {
    params.append("logisticCompanyId", logisticCompanyId);
  }

  const packages = await Axios.get(
    `packages?${params.toString()}`,
    requestConfig({ signal, timeoutMs })
  ).catch((err) => {
    return { error: err.response?.data };
  });

  return packages;
};

export const getAllPackagesByStatusForAdmin = async ({
  state,
  startDate,
  endDate,
}) => {
  const packages = await Axios.get(
    `admin-status-packages?state=${state}&startDate=${startDate}&endDate=${endDate}`
  ).catch((err) => {
    return { error: err.response.data };
  });

  return packages;
};

export const updatePackage = async ({ id, values }) => {
  try {
    const updatedPackage = await Axios.put(`packages/update/${id}`, {
      value: values,
    });
    
    return { success: true, data: updatedPackage.data, error: null };
  } catch (err) {
    // El toast lo muestra el llamador (drawer de edición, cambiar estado,
    // reasignar) para no duplicarlo ni tapar el texto del backend.
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al actualizar el paquete. Por favor intenta nuevamente.",
      context: "Actualizar paquete"
    });
    
    return { success: false, data: null, error: errorInfo };
  }
};

/**
 * Corrige la dirección de entrega de un pedido que todavía no salió a reparto
 * (comercio u operador). El backend decide quién puede y en qué estado: esto
 * solo transporta la respuesta.
 *
 * Sin toast: cada rechazo pide un mensaje distinto y lo elige el llamador con
 * `error.status` y el cuerpo en `error.data` (409 ADDRESS_EDIT_LOCKED trae
 * `reason: 'in_route' | 'closed'` y `currentStatus`; 403 trae
 * `code: 'ADDRESS_EDIT_ROLE_NOT_ALLOWED'`).
 *
 * @param {string} packageId
 * @param {{street_name: string, street_number?: string, comment?: string,
 *   zip_code?: string, municipality: {name: string}, city: {name: string},
 *   location?: {latitude: number, longitude: number}}} payload `city` es la REGIÓN.
 * @returns {Promise<{success: boolean, data: {changed: boolean, package: object}|null, error: object|null}>}
 */
export const updateDeliveryAddress = async (packageId, payload) => {
  try {
    const response = await Axios.patch(`packages/${packageId}/delivery-address`, payload);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      context: "Editar dirección de entrega"
    });
    return { success: false, data: null, error: errorInfo };
  }
};

// Bitácoras de paquetes
export const createPackageLog = async ({ packageId, type, subtype, comment }) => {
  try {
    const response = await Axios.post(`packages/${packageId}/logs`, {
      recordType: 'log',
      type,
      subtype,
      comment,
    });
    
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: true,
      customMessage: "Error al guardar la bitácora. Por favor intenta nuevamente.",
      context: "Crear bitácora"
    });
    
    return { success: false, data: null, error: errorInfo };
  }
};

// `silent`: no toast on failure, for background lookups the user did not ask for.
export const getPackageLogs = async (packageId, { silent = false } = {}) => {
  try {
    const response = await Axios.get(`packages/${packageId}/logs`);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: !silent,
      customMessage: "Error al cargar las bitácoras. Por favor intenta nuevamente.",
      context: "Obtener bitácoras"
    });
    
    return { success: false, data: null, error: errorInfo };
  }
};

// Incidencias de paquetes
//
// Segundo argumento opcional `{ silent, timeoutMs, signal }`: con `silent` no
// hay toast (el llamador muestra el error en línea, ver
// utils/incidentsApiErrors). Sin él, el comportamiento es el de siempre.

/**
 * Alta de una incidencia. Responde 201 al crear y 200 si es la repetición de un
 * alta ya hecha con la misma `idempotencyKey` (ambos son éxito).
 *
 * `idempotencyKey` va en el body y no como header: el CORS del BFF no admite
 * headers propios. `origin` es opaco para este cliente:
 * `{ source: 'helpdesk', conversationId, caseNumber?, ticketNumber? }`.
 * Un backend sin esos campos los ignora.
 */
export const createPackageIncident = async (
  { packageId, severity, responsible, type, description, origin, idempotencyKey },
  { silent = false, timeoutMs, signal } = {}
) => {
  try {
    const response = await Axios.post(
      `packages/${packageId}/incidents`,
      {
        recordType: 'incident',
        severity,
        responsible,
        incidentType: type, // El backend espera 'incidentType'
        description,
        ...(origin && { origin }),
        ...(idempotencyKey && { idempotencyKey }),
      },
      requestConfig({ timeoutMs, signal })
    );

    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = incidentRequestError(err, {
      silent,
      customMessage: "Error al guardar la incidencia. Por favor intenta nuevamente.",
      context: "Crear incidencia"
    });

    return { success: false, data: null, error: errorInfo };
  }
};

export const getPackageIncidents = async (packageId, { silent = false, timeoutMs, signal } = {}) => {
  try {
    const response = await Axios.get(
      `packages/${packageId}/incidents`,
      requestConfig({ timeoutMs, signal })
    );
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = incidentRequestError(err, {
      silent,
      customMessage: "Error al cargar las incidencias. Por favor intenta nuevamente.",
      context: "Obtener incidencias"
    });

    return { success: false, data: null, error: errorInfo };
  }
};

export const getPackageIncident = async (incidentId, { silent = false, timeoutMs, signal } = {}) => {
  try {
    const response = await Axios.get(
      `packages/incidents/${incidentId}`,
      requestConfig({ timeoutMs, signal })
    );
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = incidentRequestError(err, {
      silent,
      customMessage: "Error al cargar la incidencia. Por favor intenta nuevamente.",
      context: "Obtener incidencia"
    });

    return { success: false, data: null, error: errorInfo };
  }
};

export const updatePackageIncident = async ({ incidentId, comment, resolved }) => {
  try {
    const response = await Axios.patch(`packages/incidents/${incidentId}`, {
      comment,
      resolved,
    });
    
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: true,
      customMessage: "Error al actualizar la incidencia. Por favor intenta nuevamente.",
      context: "Actualizar incidencia"
    });
    
    return { success: false, data: null, error: errorInfo };
  }
};

export const updateIncidentSac = async ({ incidentId, comment, resolved }) => {
  try {
    const response = await Axios.patch(`packages/incidents/${incidentId}/sac`, {
      comment,
      resolved,
    });
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al actualizar SAC de la incidencia.",
      context: "Actualizar SAC incidencia"
    });
    return { success: false, data: null, error: errorInfo };
  }
};

export const updateIncidentAdministration = async ({ incidentId, paymentStatus, amount, paymentDate, paymentNotes, resolved }) => {
  try {
    const body = {};
    if (paymentStatus !== undefined) body.paymentStatus = paymentStatus;
    if (amount !== undefined) body.amount = amount;
    if (paymentDate !== undefined) body.paymentDate = paymentDate;
    if (paymentNotes !== undefined) body.paymentNotes = paymentNotes;
    if (resolved !== undefined) body.resolved = resolved;

    const response = await Axios.patch(`packages/incidents/${incidentId}/administration`, body);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al actualizar Administración de la incidencia.",
      context: "Actualizar Administración incidencia"
    });
    return { success: false, data: null, error: errorInfo };
  }
};

export const updateIncidentSupervision = async ({ incidentId, observations, resolved }) => {
  try {
    const body = {};
    if (observations !== undefined) body.observations = observations;
    if (resolved !== undefined) body.resolved = resolved;

    const response = await Axios.patch(`packages/incidents/${incidentId}/supervision`, body);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al actualizar Supervisión de la incidencia.",
      context: "Actualizar Supervisión incidencia"
    });
    return { success: false, data: null, error: errorInfo };
  }
};

export const addIncidentSanction = async ({ incidentId, driverName, driverId, reason, amount }) => {
  try {
    const response = await Axios.post(`packages/incidents/${incidentId}/supervision/sanctions`, {
      driverName,
      driverId,
      reason,
      amount,
    });
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al agregar la sanción.",
      context: "Agregar sanción"
    });
    return { success: false, data: null, error: errorInfo };
  }
};

export const removeIncidentSanction = async ({ incidentId, sanctionId }) => {
  try {
    const response = await Axios.delete(`packages/incidents/${incidentId}/supervision/sanctions/${sanctionId}`);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "Error al eliminar la sanción.",
      context: "Eliminar sanción"
    });
    return { success: false, data: null, error: errorInfo };
  }
};

/** Fecha para query params del dashboard reports: ISO YYYY-MM-DD (acepta MM/DD/YYYY del admin). */
const toReportsDashboardIsoDate = (date) => {
  if (date == null || date === '') return date;
  const s = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dateParts = s.split('/');
  if (dateParts.length === 3) {
    return `${dateParts[2]}-${dateParts[0].padStart(2, '0')}-${dateParts[1].padStart(2, '0')}`;
  }
  return s;
};

// `logisticCompanyId` es OPCIONAL: omitirlo significa "todos los operadores" y
// el backend solo se lo permite al superadmin del sistema (cualquier otro rol
// recibe 400). Se arma con URLSearchParams para no mandar
// `logisticCompanyId=undefined` cuando no viene.
export const getPackagesByStatusCount = async ({ logisticCompanyId, startDate, endDate }) => {
  try {
    const params = new URLSearchParams({
      startDate: toReportsDashboardIsoDate(startDate),
      endDate: toReportsDashboardIsoDate(endDate),
    });
    if (logisticCompanyId) params.append("logisticCompanyId", logisticCompanyId);

    const response = await Axios.get(
      `reports/dashboard/packages-resume?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error al obtener count de packages por status:", error);
    throw error;
  }
};

// Función para obtener conteos de paquetes por status para clientes
// Usa el nuevo endpoint optimizado que devuelve todos los conteos en una sola llamada
// Endpoint: GET /packages/client/status-count
export const getClientPackagesByStatusCount = async ({ companyId, startDate, endDate, dateField }) => {
  try {
    const formattedStartDate = startDate ? toReportsDashboardIsoDate(startDate) : startDate;
    const formattedEndDate = endDate ? toReportsDashboardIsoDate(endDate) : endDate;

    const params = new URLSearchParams({
      companyId,
      ...(formattedStartDate && { startDate: formattedStartDate }),
      ...(formattedEndDate && { endDate: formattedEndDate }),
      ...(dateField && { dateField }),
    });

    const response = await Axios.get(`packages/client/status-count?${params.toString()}`);
    
    return {
      status0: response.data?.status0 || 0,
      status100: response.data?.status100 || 0,
      status1: response.data?.status1 || 0,
      status3_30: response.data?.status3_30 || 0,
      status4: response.data?.status4 || 0,
      status6_7: response.data?.status6_7 || 0,
    };
  } catch (error) {
    console.error("Error al obtener count de packages por status para cliente:", error);
    return {
      status0: 0,
      status100: 0,
      status1: 0,
      status3_30: 0,
      status4: 0,
      status6_7: 0,
    };
  }
};


// Conteo de logística inversa (devoluciones) para la vista de cliente.
//
// POR QUÉ NO USA status-count: el endpoint `packages/client/status-count` NO
// devuelve los estados de la rama inversa (21/22/31/41). Verificado contra el
// backend: solo responde status0, status100, status1, status3_30, status4 y
// status6_7. Hasta que el backend agregue la clave (idealmente `status21_22`,
// como ya hace `reports/dashboard/packages-resume` para admin), el conteo se
// deriva del listado normal pidiendo `limit: 1` y leyendo `totalDocs` — el
// mismo patrón que usa la vista admin (`resume.jsx`) para los pendientes.
//
// El scope por comercio lo aplica el backend desde el JWT (igual que
// `getAllPackages`, que tampoco manda el companyId en la query).
export const RETURN_STATUSES = [21, 22, 31, 41];

export const getClientReturnsCount = async ({ startDate, endDate }) => {
  try {
    const params = new URLSearchParams({
      page: 1,
      limit: 1,
      orderField: "createdAt",
      sortType: "desc",
    });
    RETURN_STATUSES.forEach((s) => params.append("status", s));
    if (startDate) {
      params.append("startDateCreation", toReportsDashboardIsoDate(startDate));
      params.append("endDateCreation", toReportsDashboardIsoDate(endDate));
    }

    const response = await Axios.get(`packages?${params.toString()}`);
    const payload = response.data?.data ?? response.data;
    return payload?.totalDocs || 0;
  } catch (error) {
    console.error("Error al obtener el conteo de devoluciones del cliente:", error);
    return 0;
  }
};

export const createBulkPackages = async ({ packages, user, senderAddress, senderNeighborhood, senderState }) => {
  try {
    const response = await Axios.post(`packages/bulk`, {
      packages, // Array de paquetes en formato RequestCreatePackagesDto
      user,
      senderAddress, // Incluir la dirección de retiro, como en loadFile
      senderNeighborhood,
      senderState,
    }, {
      timeout: 300000, // 5 minutos para cargas masivas de Excel
    });
    // El backend devuelve { data: createdPackages }, extraer el array directamente
    const packagesArray = response.data?.data || [];
    // Devolver directamente el array para simplificar el manejo en el frontend
    return { data: packagesArray, error: null };
  } catch (err) {
    console.error("Error in bulk package creation:", err.response?.data || err.message || 'Unknown error');
    
    // Extraer el mensaje de error del backend
    const errorData = err.response?.data || {};
    let errorMessage = errorData.message || 'Error al crear los paquetes';
    let errorCode = errorData.code;
    let orderId = errorData.order_id;
    
    // Si es un error de order_id duplicado, formatear el mensaje
    if (errorCode === 'DUPLICATE_ORDER_ID' && orderId) {
      errorMessage = `Ya existe el número de venta "${orderId}". Por favor, corrija los datos e intente nuevamente.`;
    }
    
    return { 
      error: {
        message: errorMessage,
        code: errorCode,
        order_id: orderId,
        ...errorData
      }
    };
  }
};

export const updatePackagesStatus = async ({ ids, newStatus }) => {
  const updatedPackage = await Axios.put(`packages/update-packages-status`, {
     ids, newStatus
  }).catch((err) => {
    return { error: err.response.data };
  });

  return updatedPackage;
};

/**
 * Marca en LOTE varios paquetes como "Entrega fallida" (status 6 = dirección
 * incorrecta, 7 = sin moradores) reutilizando el mismo flujo que la app del
 * repartidor. Multipart: una sola foto de evidencia se reutiliza para todo el lote.
 * El backend autoriza por rol y ACOTA el universo al tenant del JWT.
 *
 * @param {Object}   p
 * @param {string[]} p.ids       _id de los paquetes seleccionados.
 * @param {number}   p.status    6 | 7.
 * @param {string}   p.batchId   uuid estable del lote (idempotencia ante reintentos).
 * @param {File}     p.file      Foto de evidencia compartida (obligatoria).
 * @param {Object}   [p.failed]  { option?, description?, selectedOptions? } del formulario.
 * @returns {Promise<{success:boolean, data:{requested:number, processed:number, ok:string[], rejected:{id:string,reason:string}[]}|null, error:Object|null}>}
 */
export const bulkFailedDelivery = async ({ ids, status, batchId, file, failed }) => {
  try {
    const fd = new FormData();
    fd.append("ids", JSON.stringify(ids || []));
    fd.append("status", String(status));
    fd.append("batchId", batchId);
    if (failed) fd.append("failed", JSON.stringify(failed));
    fd.append("file", file, file?.name || "evidencia.jpg");

    const response = await Axios.post(`packages/bulk-failed-delivery`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { success: true, data: response.data, error: null };
  } catch (err) {
    // El panel muestra `error.message` una sola vez (hoy salían dos toasts
    // idénticos: uno de acá y otro del llamador).
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "No se pudo aplicar el cambio masivo de estado.",
      context: "Cambio masivo entrega fallida",
    });
    return { success: false, data: null, error: errorInfo };
  }
};

// Obtener todos los incidentes con filtros.
// `status` admite varios estados separados por coma ('abierta,procesando').
// `excludeIncidentTypes` (arreglo o lista separada por coma) saca tipos del
// listado; vacío no se envía, porque el backend rechaza una lista vacía.
export const getAllIncidents = async ({
  page = 1,
  limit = 10,
  orderField = 'createdAt',
  sortType = 'desc',
  responsible,
  status,
  department,
  severity,
  incidentType,
  excludeIncidentTypes,
  incidentCategory,
  startDate,
  endDate,
  clientId,
} = {}, { silent = false, timeoutMs, signal } = {}) => {
  try {
    const excludedTypes = Array.isArray(excludeIncidentTypes)
      ? excludeIncidentTypes.filter(Boolean).join(',')
      : excludeIncidentTypes;
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      orderField,
      sortType,
      ...(responsible && { responsible }),
      ...(status && { status }),
      ...(department && { department }),
      ...(severity && { severity }),
      ...(incidentType && { incidentType }),
      ...(excludedTypes && { excludeIncidentTypes: excludedTypes }),
      ...(incidentCategory && { incidentCategory }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(clientId && { clientId }),
    });

    const response = await Axios.get(
      `packages/incidents?${params.toString()}`,
      requestConfig({ timeoutMs, signal })
    );

    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = incidentRequestError(err, {
      silent,
      customMessage: "Error al cargar los incidentes. Por favor intenta nuevamente.",
      context: "Obtener incidentes"
    });

    return { success: false, data: null, error: errorInfo };
  }
};

// Timeout explícito de las llamadas del panel de incidencias (el global de
// axios es de 60 s).
const PHONE_SEARCH_TIMEOUT_MS = 15000;

/**
 * Error de la búsqueda por teléfono, reducido a lo que la interfaz necesita.
 * No pasa por errorHandler: su log incluye el error de axios completo, y la
 * config de la request lleva los teléfonos en el body. Por eso el log es solo
 * status y code.
 * @returns {{status: number|null, code: string|null, retryAfterSeconds?: number,
 *   invalidIndexes?: number[], maxDays?: number, canceled?: true}}
 */
const phoneSearchError = (err) => {
  if (isRequestCanceled(err)) return { status: null, code: null, canceled: true };

  const status = typeof err?.response?.status === "number" ? err.response.status : null;
  const body = err?.response?.data;
  const code = typeof body?.code === "string" ? body.code : null;
  const error = { status, code };
  // El header Retry-After no es legible cross-origin: el backend lo repite en el body.
  if (Number.isFinite(body?.retryAfterSeconds) && body.retryAfterSeconds > 0) {
    error.retryAfterSeconds = Math.ceil(body.retryAfterSeconds);
  }
  if (Array.isArray(body?.invalidIndexes)) {
    error.invalidIndexes = body.invalidIndexes.filter(Number.isInteger);
  }
  if (Number.isFinite(body?.maxDays)) error.maxDays = body.maxDays;

  console.error("[Buscar paquetes por teléfono] Error:", { status, code });
  return error;
};

/**
 * Paquetes cuyo destinatario tiene alguno de los teléfonos. POST con los
 * teléfonos SOLO en el body: así no quedan en la URL, el historial ni los logs
 * de acceso. Nunca muestra toast; el llamador traduce el error con
 * utils/incidentsApiErrors.
 *
 * @param {{phones: string[], from?: string, to?: string, limit?: number, incident?: 'any'|'open'}} query
 *   `from` inclusivo y `to` exclusivo (ISO); la página siguiente se pide con
 *   `to` = `createdAt` del último ítem.
 * @param {{signal?: AbortSignal, timeoutMs?: number}} [options]
 * @returns {Promise<{success: boolean, data: object|null, error: object|null}>}
 */
export const searchPackagesByReceiverPhones = async (
  { phones, from, to, limit, incident } = {},
  { signal, timeoutMs = PHONE_SEARCH_TIMEOUT_MS } = {}
) => {
  try {
    const response = await Axios.post(
      "packages/receiver-phone-search",
      {
        phones,
        ...(from && { from }),
        ...(to && { to }),
        ...(limit != null && { limit }),
        ...(incident && { incident }),
      },
      requestConfig({ signal, timeoutMs })
    );
    return { success: true, data: response.data, error: null };
  } catch (err) {
    return { success: false, data: null, error: phoneSearchError(err) };
  }
};


export const previewWhatsappAlerts = async (payload) => {
  try {
    const response = await Axios.post(`packages/alerts/whatsapp/preview`, payload);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      fallbackMessage: "No se pudo obtener la vista previa de WhatsApp. Inténtalo nuevamente.",
      context: 'WhatsApp alerts preview'
    });
    return { success: false, data: null, error: errorInfo };
  }
};

/**
 * Retorna las coordenadas (latitude, longitude) de paquetes entregados
 * en el rango de fecha indicado, filtradas por la empresa logística del usuario autenticado.
 * @param {string} dateFrom - Fecha inicio (YYYY-MM-DD)
 * @param {string} dateTo   - Fecha fin (YYYY-MM-DD)
 */
export const getDeliveryCoords = async (dateFrom, dateTo) => {
  try {
    const params = new URLSearchParams({ dateFrom, dateTo });
    const response = await Axios.get(`packages/delivery-coords?${params.toString()}`);
    return { success: true, data: response.data, error: null };
  } catch (err) {
    const errorInfo = errorHandler.handleApiError(err, {
      showNotification: false,
      context: 'getDeliveryCoords',
    });
    return { success: false, data: null, error: errorInfo };
  }
};


// ── Direcciones por resolver ──────────────────────────────────────────────
//
// Paquetes cuya dirección el geocoder no pudo ubicar con precisión de calle Y
// número. Quedan SIN coordenada, así que no entran a la ruta optimizada: esto
// no es un informe, es trabajo que hay que hacer antes de que salga el reparto.
//
// El backend acota por empresa a partir del JWT; el front nunca manda el id del
// operador ni del comercio.

/** Listado paginado. `params`: { page, limit }. */
export const getPendingAddresses = (params) =>
  Axios.get("/packages/pending-addresses", { params });

/** Sólo el número, para el contador del menú. */
export const getPendingAddressesCount = () =>
  Axios.get("/packages/pending-addresses/count");

/**
 * Fija a mano la coordenada de un paquete pendiente.
 * `data`: { lat, lng, label? }.
 */
export const resolvePendingAddress = (packageId, data) =>
  Axios.patch(`/packages/pending-addresses/${packageId}/resolve`, data);
